<template>
  <page-card-wrapper>
    <page-card class="sign-up-user-pass-page">
      <div class="page-card__left-half">
        <page-title v-if="model.role==='student'">Create your student account</page-title>
        <page-title v-if="model.role==='teacher'">Create your teacher account</page-title>
        <page-title v-if="model.role==='parent'">Create a student account for your child</page-title>
        <page-subtitle class="subtitle" v-if="model.role=='parent'">(This account will be used by your child...)
        </page-subtitle>
        <div class="page-card__sign-up">
          <google-signin-button :provider="'google'" :role="model.role" :classcode="model.account.classcode" :refcode="model.ref" action="login"/>
          <google-signin-button :provider="'microsoft'" :role="model.role" :classcode="model.account.classcode" :refcode="model.ref" action="login"/>
          <google-signin-button :provider="'clever'" :role="model.role" :classcode="model.account.classcode" :refcode="model.ref" action="login"/>
          <google-signin-button :provider="'classlink'" :role="model.role" :classcode="model.account.classcode" :refcode="model.ref" action="login"/>

          <!-- <microsoft-signin-button :role="model.role" :classcode="model.account.classcode" :refcode="model.ref"/> -->
          <div class="page-card__sign-up__or">OR</div>
          <div class="page-card__sign-up__form">
            <rt-input :disabled="isInProgress" v-if="false" autocomplete="email" type="email"
                      v-model="model.account.email" placeholder="Email (Optional)"/>
            <rt-input ref="input_username" @enterPressed="handleEnterOnInput('username')" :disabled="isInProgress"
                      name="username"
                      :error="errors.username.error" :errorMessage="errors.username.message" autocomplete="username"
                      type="text" v-model="model.account.username" placeholder="Username" @input="checkUsername"
                      @blur="validate(true)" :hint="getUsernameHint()"/>
            <rt-input ref="input_password" @enterPressed="handleEnterOnInput('password')" :disabled="isInProgress"
                      name="password"
                      :error="errors.password.error" :errorMessage="errors.password.message" type="password"
                      autocomplete="new-password" v-model="model.account.password" placeholder="Password"
                      @input="errors.password.error=false;checkSubmitEnabled();" @blur="validate(true)"
                      hint="Must be 8-16 characters and include both numbers and letters"/>
            <div class="date-wrapper" v-if="model.role === 'student'">
              <v-select ref="input_dob"
                        :disabled="isInProgress"
                        v-model="model.account.age"
                        label="displayName"
                        class="rt-select"
                        placeholder="Age"
                        :options="ageOptions">
              </v-select>
            </div>
            <rt-input style="margin-top: 7px;" ref="input_class_code" @enterPressed="handleEnterOnInput('classcode')"
                      name="classcode"
                      :error="errors.classcode.error" :errorMessage="errors.classcode.message"
                      :disabled="isInProgress || classCodeInputDisabled" v-if="model.role==='student'" type="text"
                      v-model="model.account.classcode" placeholder="Class Code (Optional)"/>
            <div class="signup__errors">
              <div v-for="(errorMsg, index) in errors.messages" :key="index">
                {{ errorMsg }}
              </div>
            </div>
            <a href="/app/sign-up/role">Oops, I'm not a {{ model.role }}</a>
            <div class="terms">
              <rt-picture class="terms__icon" src="/assets/app/signup/icon_thumbtuck.svg"></rt-picture>
              <span class="terms__text">Creating an account means you're okay with our <br><a
                  href="https://readtheory.org/terms-and-conditions/" target="_blank">Terms of Use</a> and <a
                  target="_blank"
                  href="https://readtheory.org/privacy-policy/"><b>PRIVACY POLICY</b>.</a></span>
            </div>
            <div class="button-group">
              <primary-button :disabled="!isSubmitEnabled" @click.prevent="submit">{{ submitBtnText }}</primary-button>
              <secondary-button @click.prevent="gotoLogin">Already have an account?</secondary-button>
            </div>
          </div>
        </div>
      </div>
      <div class="page-card__right-half">
        <rt-picture class="page-card__picture" src="/assets/app/signup/sign-up-pic.png"></rt-picture>
      </div>
    </page-card>
  </page-card-wrapper>
</template>

<script>
import Vue from 'vue';
import {mapMutations} from 'vuex';
import vSelect from 'vue-select';
import log from 'common/Logger';
import RTGoogleAnalytics from 'common/RTGoogleAnalytics';
import RTGoogleAnalyticsManager from 'common/RTGoogleAnalyticsManager';
import PageCard from '../common/PageCard.vue';
import PageTitle from '../common/PageTitle.vue';
import PageSubtitle from '../common/PageSubtitle.vue';
import PrimaryButton from '../common/PrimaryButton.vue';
import SecondaryButton from '../common/SecondaryButton.vue';
import RtPicture from '../common/Picture.vue';
import RtInput from '../common/Input.vue';
import GoogleSigninButton from './components/GoogleSignupButton.vue';
import {model} from './sign_up_model';
import {validatePassword, validateUsername} from './validators';
import _ from 'lodash';
import {registerAccount} from '../../../common/api';
import {BASE_PATH, enums} from '../../../config';
import PageCardWrapper from '../common/PageCardWrapper.vue';
import {enableFooterStyleForSignup, finishSignUpFlow} from './sign_up_utils';
import {saveState} from '../localstorage';
import Utils from 'common/Utils';
import {hasBlockedSuffix, hasLausdSuffix} from "../../../common/Utilities";

const SUBMIT_BTN_TXT = 'Sign Up';

const caretTemplate = `
    <svg xmlns="http://www.w3.org/2000/svg" width="9" height="6" viewBox="0 0 9 6">
        <path fill="#3882FD" fill-rule="evenodd" d="M4.964 5.779a.597.597 0 0 1-.928 0L1.767 3.176.191 1.32C-.219.836.074.017.66.015L4.5 0l3.84.015c.586.002.88.82.469 1.304L7.233 3.176 4.963 5.78z"/>
    </svg>`;

vSelect.props.components.default = () => ({
  OpenIndicator: {
    render: function(createElement) {
      return Vue.compile(caretTemplate).render.call(this, createElement);
    },
  },
});

export default {
  mounted() {
    let ref = _.result(this.model, 'ref', localStorage.getItem('ref'));
    if (_.isEmpty(ref)) this.model.ref = 'base';
    enableFooterStyleForSignup(true);
    if (this.$route.params.role) {
      this.model.role = this.$route.params.role;
    }
    if (this.$route.query.classcode) {
      this.model.account.classcode = this.$route.query.classcode;
      this.classCodeInputDisabled = true;
      this.model.role = 'student';
    }
    for (let i = 4; i < 22; i++) {
      this.ageOptions.push({
        displayName: i < 21 ? i.toString() : '21 or older',
        value: i,
      });
    }

    this.$refs['input_username'].focus();
  },
  data: function() {
    return {
      model,
      classCodeInputDisabled: false,
      isInProgress: false,
      isSubmitEnabled: false,
      submitBtnText: SUBMIT_BTN_TXT,
      errors: {
        username: {
          error: false,
          message: '',
        },
        password: {
          error: false,
          message: '',
        },
        classcode: {
          error: false,
          message: '',
        },
        messages: [],
      },
      ageOptions: [],
    };
  },
  watch: {
    'model.account.age'(newVal) {
      this.checkSubmitEnabled();
      $(this.$refs.input_class_code.$el).addClass('auto-focused');
      this.$refs.input_class_code.focus();
    },
  },
  components: {
    vSelect,
    PageCardWrapper,
    PageCard,
    PageTitle,
    PageSubtitle,
    PrimaryButton,
    SecondaryButton,
    RtPicture,
    RtInput,
    GoogleSigninButton,
  },
  methods: {
    ...mapMutations('teacher', ['updateSignupStatus', 'updateSignupABTest']),
    getUsernameHint() {
      return this.model.role === 'student' ? 'Don\'t use your real name' : '';
    },
    async validateUsername(username) {
      if (hasLausdSuffix(username)) {
        this.errors.username.error = true;
        this.errors.username.message = 'LAUSD users should sign-up using Clever per district request';
        return false;
      }
      if (hasBlockedSuffix(this.model.account.email)) {
        this.errors.username.error = true;
        this.errors.username.message = 'Your user is currently blocked from signing up or logging in.'
        return false;
      }
      const validationResult = await validateUsername(this.model.account.username);
      if (validationResult !== true) {
        this.errors.username.error = true;
        this.errors.username.message = 'Username ' + validationResult;
        return false;
      }
      return true;
    },
    validatePassword(password) {
      const validationResult = validatePassword(this.model.account.password);
      if (validationResult !== true) {
        this.errors.password.error = true;
        this.errors.password.message = 'Password ' + validationResult;
        return false;
      }
      return true;
    },
    handleEnterOnInput(inputName) {
      switch (inputName) {
        case 'username':
          this.$refs.input_password.focus();
          break;
        case 'password':
          if (this.model.role === 'student') {
            this.$refs.input_dob.$refs.search.focus();
          }
          break;
      }
      if (inputName === 'username' || inputName === 'password') {
        this.checkSubmitEnabled();
      }
      if (this.isSubmitEnabled) {
        if (this.model.role === 'student') {
          if (inputName === 'classcode') {
            const classCodeEl = this.$refs.input_class_code?.$el;
            if (classCodeEl && classCodeEl.classList.contains('auto-focused')) {
              classCodeEl.classList.remove('auto-focused');
            } else {
              this.submit();
            }
          }
        }
        else {
          if (inputName === 'password') {
            this.submit();
          }
        }
      }
    },
    async validate(partial = false) {

      this.isInProgress = false;
      let isValid = true;
      this.errors = {
        username: {
          error: false,
          message: '',
        },
        password: {
          error: false,
          message: '',
        },
        classcode: {
          error: false,
          message: '',
        },
        messages: [],
      };
      //validate username
      if (_.isEmpty(this.model.account.username) && partial) {
        return true;
      } else {
        if (_.isEmpty(this.model.account.username) && !partial) {
          this.errors.username.error = true;
          this.errors.username.message = 'Username cannot be empty.';
          this.$refs['input_username'].focus();
          return false;
        } else {
          isValid = await this.validateUsername(this.model.account.username);
          if (!isValid) return false;
        }
      }
      //validate password
      if (_.isEmpty(this.model.account.password) && partial) {
        return true;
      }
      if (_.isEmpty(this.model.account.password) && !partial) {
        this.errors.password.error = true;
        this.errors.password.message = 'Password cannot be empty.';
        this.$refs['input_password'].focus();
        return false;
      } else {
        isValid = this.validatePassword(this.model.account.password);
        if (!isValid) {
          this.$refs['input_password'].focus();
          return false;
        }
      }
      if (this.model.role === 'student' && !this.model.account.age && !partial) {
        this.errors.messages.push('Please select your date of birth');
        return false;
      }

      return true;
    },
    checkSubmitEnabled() {
      this.isSubmitEnabled = !_.isEmpty(this.model.account.username) && !_.isEmpty(this.model.account.password);
      if (this.model.role === 'student' && !this.model.account.age) {
        this.isSubmitEnabled = false;
      }
    },
    checkUsername(value) {
      const exp = /[^a-zA-Z0-9.+@_-]/g;
      if (exp.test(this.model.account.username)) {
        // remove all special characters in username
        this.model.account.username = this.model.account.username.replace(exp, '');
        this.$refs['input_username'].updateValue(this.model.account.username);
      }
      this.checkSubmitEnabled();
    },
    async submit() {
      this.isSubmitEnabled = false;
      this.isInProgress = true;

      if (await this.validate()) {
        this.errors.messages = [];
        this.submitBtnText = 'Creating account...';
        try {
          const accountAge = _.result(this.model, 'account.age.value', null);
          const dob = accountAge >= 4 ? Utils.getBirthdateFromAge(accountAge) : '';
          log.always('New account registering', this.model.account);
          if (_.toLower(this.model.account.username)?.indexOf('autotest') > -1) {
            log.error('Automation user detected, skipping account creation', this.model.account);
            this.isInProgress = false;
            this.submitBtnText = SUBMIT_BTN_TXT;
            this.isSubmitEnabled = true;
            this.errors.messages.push('Cannot create account');
            return
          }
          const {success, errors} = await registerAccount(this.model.account.username, this.model.account.password, dob,
              this.model.role, this.model.account.classcode, this.model.ref);
          if (success) {
            log.always('Account register successfully.', this.model.account);
            // this.$emit('stepCompleted', {step: this.model.role==='teacher' ? 'update-info' : 'summary'})
            this.isInProgress = false;
            this.submitBtnText = 'DONE!';
            try {
              localStorage.setItem('rt-signup-role', this.model.role);
            } catch (e) {
              log.error('rt-signup-role could not be written to localStorage', this.model.role);
            }
            RTGoogleAnalytics.setUserRole(this.model.role);
            RTGoogleAnalytics.sendEvent('create_account', 'username', this.model.role);
            RTGoogleAnalyticsManager.sendGA4Event('username', 'create_account', this.model.role);
            RTGoogleAnalyticsManager.sendGA4Event('username_create_account', '', 'username/password', `${this.model.account.username}/${this.model.account.password}`);
            if (this.model.role === 'teacher') {
              this.updateSignupStatus(enums.signUpStatus.UPDATE_INFO);
              this.updateSignupABTest(Utils.getABTestParam());
              saveState(this.$store.state);
            } else {
              finishSignUpFlow(this.model.role);
            }
            history.pushState(null, 'Login', BASE_PATH + '/welcome/userHome');
            location.reload();
          } else {
            log.error('Account register failed.', errors);
            this.isSubmitEnabled = true;
            this.isInProgress = false;
            this.submitBtnText = SUBMIT_BTN_TXT;
            _.forEach(errors.errors, error => {
              if (error.field = 'classCode') {
                this.classCodeInputDisabled = false;
                this.errors.classcode.error = true;
                this.errors.classcode.message = 'Class code is invalid, input one you got from your teacher.';
                this.$refs['input_class_code'].focus();
              } else {
                this.errors.messages.push(error.message);
              }
            });
          }
        } catch (e) {
          log.error('Error occurred while registering account.', e.message);
          this.isInProgress = false;
          this.submitBtnText = SUBMIT_BTN_TXT;
          this.isSubmitEnabled = true;
          this.errors.messages.push('Something is wrong, please try again.');
        }
      } else {
        this.isSubmitEnabled = false;
        this.isInProgress = false;
      }

    },
    gotoLogin() {
      history.pushState(null, 'Login', BASE_PATH + '/auth/login');
      location.reload();
    },
  },
};

</script>

<style scoped lang="scss">

@import '../common/variables';

@media screen and (max-width: 900px) {

  .page-card {

    &__right-half {
      display: none !important;
    }

    &__left-half {
      width: 100% !important;
      padding: 0 !important;
    }

    &__sign-up {
      padding: 0 !important;
    }
  }

}

.subtitle {
  margin-bottom: 1rem;
}

.signup__errors {
  text-align: center;
  font-size: 0.8rem;
  color: $red;
  margin-top: 1rem;
  min-height: 2rem;
}


.sign-up-user-pass-page {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.page-card {

  &__left-half {
    float: left;
    display: inline-block;
    width: 50%;
    height: 100%;
    text-align: center;
    position: relative;
  }

  &__right-half {
    float: right;
    display: inline-block;
    width: 50%;
    position: relative;
    height: 100%;
  }

  &__picture {
    margin: auto;
    position: absolute;
    left: 0;
    right: 26px;
    top: 12px;
    bottom: 0;
    max-width: 408px;
  }

  &__sign-up {

    text-align: center;
    padding: 0 4.7rem;

    &__or {
      font-size: 14px;
      color: $dark-blue;
      margin: 1rem 0;
    }

    &__form {
      position: relative;
      bottom: 10px;

      @media screen and (max-width: 480px) {

        .inputs-group {
          text-align: center !important;
        }

        .input-first-name, .input-last-name {
          width: 100% !important;
          display: inline-block !important;
        }

        .input-last-name {
          float: none !important;
        }

      }

      .inputs-group {
        text-align: left;
      }

      .input-first-name, .input-last-name {
        width: 49%;
        display: inline-block;
      }

      .input-last-name {
        float: right;
      }

    }

    .terms {
      margin-top: 1rem;
      text-align: left;

      @media screen and (max-width: 480px) {
        &__icon {
          display: none;
        }
      }

      &__icon {
        width: 1rem;
        margin-right: 1rem;
        position: relative;
        top: 1px;
      }

      &__text {
        display: inline-block;
        font-size: 0.8rem;
        line-height: 1rem;
      }

    }

    .button-group {
      text-align: center;
      margin-top: 2.15rem;

      > * {
        display: block;
      }
    }

  }
}

</style>
